import BlockIcon from "@mui/icons-material/Block";
import {
	Badge,
	Card,
	CardContent,
	CardHeader,
	Container,
	Grid,
	MenuItem,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import {
	NumberField,
	PlainDate,
	PlainDatePicker,
	PlainDateTime,
	PlainDateTimePicker,
	TextField,
	ZonedDateTime,
} from "@variocube/app-ui";
import {IntegerNumberFormat} from "@variocube/app-ui/esm/Input/NumberField";
import React, {useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {DisplayUnitsCount} from "../../booking-utils";
import {Accessibility} from "../../component/accessibility";
import {useOrder} from "../../context/order";
import {useTenant} from "../../context/tenant";
import {B2Site, SiteAccessibility} from "../../data/client.gen";
import {
	getOpeningHoursEnd,
	getStartOfDay,
	getWeekdayFromDate,
	getZoneDateTimeNow,
	isValidPeriod,
	isWithinOpeningHours,
	parseTime,
	parseZoneDateTimeFromDate,
	parseZoneDateTimeFromDateTime,
	parseZoneDateTimeFromInstant,
	snapToHour,
	WEEKDAYS,
} from "../../dates-utils";
import {useLocalization} from "../../i18n";
import {computePackages, notBlank} from "../../utils";
import {OrderNav} from "./order-nav";

interface OrderDatesProps {
	site: B2Site;
}

export function OrderDates({site}: OrderDatesProps) {
	const navigate = useNavigate();
	const {t} = useLocalization();
	const {tenant} = useTenant();
	const {order: {dates: {datesValid = false, unitCounts = 0}}} = useOrder();

	return (
		<Container maxWidth="sm">
			<Card>
				<CardHeader
					title={
						<Grid
							container
							justifyContent="space-between"
							alignItems="baseline"
							spacing={2}
						>
							<Grid
								item
								xs={12}
								sm="auto"
							>
								<Typography variant="h4">{t("dates.period")}</Typography>
							</Grid>
							{(datesValid && unitCounts > 0) && (
								<Grid
									item
									xs={12}
									sm="auto"
								>
									<Typography variant="subtitle1">
										{t("dates.duration")}
										&nbsp;
										<DisplayUnitsCount unit={site.bookingUnit} count={unitCounts} />
									</Typography>
								</Grid>
							)}
						</Grid>
					}
				/>
				<CardContent>
					{site.bookingUnit === "Days" && <DayTimeframeSelector site={site} />}
					{site.bookingUnit === "Hours" && <HourTimeframeSelector site={site} />}
					<Stack
						mt={2}
						direction="row"
						justifyContent="space-between"
					>
						<Typography>{t("sites.accessibility")}:</Typography>
						<Accessibility site={site} />
					</Stack>
				</CardContent>
			</Card>

			<OrderNav
				site={site}
				onNext={() => navigate(`/${tenant?.id}/${site.id}/boxes`)}
				disabled={!datesValid}
			/>
		</Container>
	);
}

function getDefaultFrom(minDate: ZonedDateTime | undefined, zoneId: string, accessibility: SiteAccessibility) {
	const now = getZoneDateTimeNow(zoneId);
	if (minDate !== undefined && ZonedDateTime.compare(minDate, now) > 0) {
		console.log("defaultFrom: minDate is after `now`. defaulting to `minDate`", {minDate, now});
		return minDate;
	}
	if (accessibility !== undefined) {
		const yesterday = now.subtract({days: 1});
		const yesterdayAccessibility = accessibility[getWeekdayFromDate(yesterday)];
		if (yesterdayAccessibility && yesterdayAccessibility.from && yesterdayAccessibility.until) {
			if (yesterdayAccessibility.until < yesterdayAccessibility.from) {
				const until = parseTime(yesterdayAccessibility.until);
				if (until) {
					const targetUntil = now.with({
						hour: until.hour,
						minute: until.minute,
						second: until.second,
					});
					if (ZonedDateTime.compare(now, targetUntil) < 0) {
						console.log("defaultFrom: yesterday's accessibility is not over. using yesterday");
						return getStartOfDay(yesterday);
					}
				}
			}
		}
	}
	console.log("defaultFrom: defaulting to `now`");
	return getStartOfDay(now);
}

function DayTimeframeSelector({site}: { site: B2Site }) {
	const {
		activeFrom,
		activeUntil,
		notBookableDates: nbD = [],
		maxBookingUnits,
		accessibility,
		boxes,
		zoneId = "UTC",
	} = site;
	const {t} = useLocalization();
	const packages = computePackages(boxes);
	const notBookableDates = nbD.map(d => parseZoneDateTimeFromDate(d, zoneId));
	const activeFromDateTime = notBlank(activeFrom)
		? getStartOfDay(parseZoneDateTimeFromDate(activeFrom as string, zoneId))
		: undefined;
	const activeUntilDateTime = notBlank(activeUntil)
		? getStartOfDay(parseZoneDateTimeFromDate(activeUntil as string, zoneId))
		: undefined;

	const defaultFrom = getDefaultFrom(activeFromDateTime, zoneId, accessibility);

	const {order: {dates: {from, until}}, setDates} = useOrder();
	const {orderFrom, orderUntil} = useMemo(() => ({
		orderFrom: (notBlank(from))
			? parseZoneDateTimeFromDateTime(from as string, zoneId)
			: defaultFrom,
		orderUntil: (notBlank(until))
			? parseZoneDateTimeFromDateTime(until as string, zoneId)
			: undefined,
	}), [from, until, defaultFrom]);

	const maxDate = useMemo(() => {
		const maxBookingUntil = !!maxBookingUnits
			? orderFrom.add({days: maxBookingUnits - 1})
			: undefined;
		if (maxBookingUntil !== undefined && activeUntilDateTime !== undefined) {
			// return the earlier until date
			return ZonedDateTime.compare(maxBookingUntil, activeUntilDateTime) < 0
				? maxBookingUntil
				: activeUntilDateTime;
		}
		return activeUntilDateTime ?? maxBookingUntil;
	}, [orderFrom, maxBookingUnits, activeUntilDateTime]);

	function updateOrderDates(from: ZonedDateTime | undefined, until: ZonedDateTime | undefined) {
		const siteMaxDate = site.pricingMode === "Package"
			? undefined
			: maxDate;
		const unitCounts = !from || !until ? 0 : from.toPlainDate().until(until.toPlainDate()).days + 1;
		console.log("unitCounts", unitCounts);
		setDates({
			from: from?.toPlainDate().toJSON(),
			until: until?.toPlainDate().toJSON(),
			unitCounts,
			datesValid: isValidPeriod(from, until, activeFromDateTime, siteMaxDate),
		});
	}

	function handleFromChange(from: PlainDate | null) {
		const parsedFrom = from?.toZonedDateTime(zoneId);
		updateOrderDates(parsedFrom, orderUntil);
	}

	function handleUntilChange(until: PlainDate | null) {
		const parsedUntil = until?.toZonedDateTime(zoneId);
		updateOrderDates(orderFrom, parsedUntil);
	}

	const [selectUnits, setSelectUnits] = useState(0);
	function handleUnitChange(units: number) {
		setSelectUnits(units);
		handleUntilChange(orderFrom.add({days: units - 1}).toPlainDate());
	}

	return (
		<Grid
			container
			spacing={2}
		>
			<Grid
				item
				xs={12}
				md={6}
			>
				<PlainDatePicker
					required
					fullWidth
					label={t("dates.from.label")}
					helperText={t("dates.from.hint")}
					value={orderFrom.toPlainDate()}
					onChange={handleFromChange}
					minDate={defaultFrom.toPlainDate()}
					renderDay={day =>
						notBookableDates.find(d => PlainDate.compare(d, day) === 0)
							? (
								<Tooltip title={t("dates.unavailable")}>
									<Badge
										badgeContent={
											<BlockIcon
												sx={{fontSize: "15px", position: "relative", top: -1, right: -2}}
												color="error"
											/>
										}
									>
										<span>{day.day}</span>
									</Badge>
								</Tooltip>
							)
							: <span>{day.day}</span>}
				/>
			</Grid>
			<Grid
				item
				xs={12}
				md={6}
			>
				{"Package" === site?.pricingMode && (
					<TextField
						select
						fullWidth
						required
						label={t("dates.package")}
						value={selectUnits}
						onChange={value => handleUnitChange(parseInt(value))}
						InputLabelProps={{
							shrink: true,
						}}
					>
						{packages.map(({units}, i) => (
							<MenuItem key={"package-" + i} value={units}>
								<DisplayUnitsCount unit={site.bookingUnit} count={units} />
							</MenuItem>
						))}
					</TextField>
				)}
				{"Unit" === site.pricingMode && (
					<PlainDatePicker
						required
						fullWidth
						label={t("dates.until.label")}
						helperText={t("dates.until.hint")}
						value={orderUntil?.toPlainDate() ?? null}
						onChange={handleUntilChange}
						defaultCalendarMonth={orderFrom.toPlainDate()}
						minDate={orderFrom.toPlainDate()}
						maxDate={maxDate?.toPlainDate()}
						renderDay={day =>
							notBookableDates.find(d => PlainDate.compare(d, day) === 0)
								? (
									<Tooltip title={t("dates.unavailable")}>
										<Badge
											badgeContent={
												<BlockIcon
													sx={{fontSize: "15px", position: "relative", top: -1, right: -2}}
													color="error"
												/>
											}
										>
											<span>{day.day}</span>
										</Badge>
									</Tooltip>
								)
								: <span>{day.day}</span>}
					/>
				)}
			</Grid>
		</Grid>
	);
}

function HourTimeframeSelector({site}: { site: B2Site }) {
	const {
		effectiveAccessibleFrom,
		effectiveAccessibleUntil,
		earliestBookingFrom,
		accessibility,
		alwaysAccessible,
		maxBookingUnits,
		boxes,
		zoneId = "UTC",
	} = site;
	const {t} = useLocalization();
	const packages = computePackages(boxes);

	const activationFrom = notBlank(effectiveAccessibleFrom)
		? parseZoneDateTimeFromInstant(effectiveAccessibleFrom as string, zoneId)
		: undefined;
	// `earliestBookingFrom` is `now` when open, else the next opening window — gates the start to opening hours.
	const earliestFrom = notBlank(earliestBookingFrom)
		? parseZoneDateTimeFromInstant(earliestBookingFrom as string, zoneId)
		: undefined;
	// The picker's lower bound is the later of "site active from" and "next bookable moment".
	const minDate = (activationFrom !== undefined && earliestFrom !== undefined)
		? (ZonedDateTime.compare(activationFrom, earliestFrom) > 0 ? activationFrom : earliestFrom)
		: (earliestFrom ?? activationFrom);
	// Sites with no configured opening hours (and always-accessible sites) are 24/7 — skip the window check.
	const enforceOpeningHours = !alwaysAccessible
		&& WEEKDAYS.some(d => notBlank(accessibility[d]?.from) && notBlank(accessibility[d]?.until));

	// Upper bound is only the site's activation end — future days stay selectable. Per-day opening-hours can't be
	// expressed via min/max alone, so out-of-window times are caught by the validity check below (Next disabled).
	const maxDate = notBlank(effectiveAccessibleUntil)
		? parseZoneDateTimeFromInstant(effectiveAccessibleUntil as string, zoneId)
		: undefined;
	const now = getZoneDateTimeNow(zoneId);

	// Default to the next bookable moment (now if open) — keep the actual clock time, don't jump to the next hour.
	const defaultFrom = (minDate !== undefined && ZonedDateTime.compare(minDate, now) > 0)
		? minDate
		: now;

	const {order: {dates: {fromDateTime, hours}}, setDates} = useOrder();
	const {orderFrom} = useMemo(() => ({
		orderFrom: (notBlank(fromDateTime))
			? parseZoneDateTimeFromInstant(fromDateTime as string, zoneId)
			: defaultFrom,
	}), [fromDateTime, hours, defaultFrom, zoneId]);

	// The picker can't disable individual times, so an out-of-window start is explained here (and Next is disabled).
	const startOutsideHours = enforceOpeningHours && !isWithinOpeningHours(orderFrom, accessibility);

	// Largest bookable hours for a given start: min of the site's maxBookingUnits and the time left until the
	// opening window closes — so a booking can't be paid for past closing time (e.g. start 15:00 in a 12–16 site → 1h).
	function maxHoursFor(from: ZonedDateTime | undefined) {
		// Packages are fixed products whose selected duration must reach the backend verbatim — capping one to the
		// window close produces a duration no package matches ("Failed to find package pricing", #1175). Like the
		// Days selector, package pricing ignores the max limits; a package may simply outlast the opening window.
		if (site.pricingMode === "Package") {
			return undefined;
		}
		// `maxBookingUnits` of 0 / undefined means "no site limit" (matches the original truthy semantics).
		let cap = maxBookingUnits ? maxBookingUnits : undefined;
		if (from !== undefined && enforceOpeningHours) {
			const end = getOpeningHoursEnd(from, accessibility);
			if (end !== undefined) {
				const hoursToClose = Math.floor(
					(end.toInstant().epochMilliseconds - from.toInstant().epochMilliseconds) / 3_600_000,
				);
				cap = cap === undefined ? hoursToClose : Math.min(cap, hoursToClose);
			}
		}
		return cap;
	}

	function updateOrderDates(from: ZonedDateTime | undefined, hours: number | undefined) {
		const fromDateTime = from?.toInstant().toJSON();
		const withinHours = from !== undefined
			&& (!enforceOpeningHours || isWithinOpeningHours(from, accessibility));
		const notBeforeMin = from !== undefined
			&& (minDate === undefined || ZonedDateTime.compare(from, minDate) >= 0);
		const maxHours = maxHoursFor(from);
		const fitsWindow = hours === undefined || maxHours === undefined || hours <= maxHours;
		setDates({
			fromDateTime,
			hours,
			unitCounts: !hours ? 0 : hours,
			datesValid: from !== undefined && hours !== undefined && hours > 0 && withinHours && notBeforeMin
				&& fitsWindow,
		});
	}

	function handleFromChange(from: PlainDateTime | null) {
		let parsedFrom = from?.toZonedDateTime(zoneId);
		if (parsedFrom !== undefined && enforceOpeningHours) {
			parsedFrom = snapToHour(parsedFrom, minDate);
		}
		updateOrderDates(parsedFrom, hours);
	}

	function handleHoursChange(h: number | null) {
		let parsedHours = h ?? 0;
		if (parsedHours < 0) {
			parsedHours = 0;
		}
		const cap = maxHoursFor(orderFrom);
		if (cap !== undefined && parsedHours > cap) {
			parsedHours = cap;
		}
		const parsedFrom = orderFrom;
		updateOrderDates(parsedFrom, parsedHours ?? undefined);
	}

	return (
		<Grid
			container
			spacing={2}
		>
			<Grid
				item
				xs={12}
				md={6}
			>
				<PlainDateTimePicker
					required
					fullWidth
					label={t("dates.from.label")}
					helperText={startOutsideHours
						? (
							<Typography
								component="span"
								variant="inherit"
								color="error"
							>
								{t("dates.outsideOpeningHours")}
							</Typography>
						)
						: t("dates.from.hint")}
					value={orderFrom?.toPlainDateTime()}
					onChange={handleFromChange}
					disablePast={true}
					minDateTime={minDate?.toPlainDateTime()}
					maxDateTime={maxDate?.toPlainDateTime()}
				/>
			</Grid>
			<Grid
				item
				xs={12}
				md={6}
			>
				{"Package" === site?.pricingMode && (
					<TextField
						select
						fullWidth
						required
						label={t("dates.package")}
						value={hours}
						onChange={value => handleHoursChange(parseInt(value))}
						InputLabelProps={{
							shrink: true,
						}}
					>
						{packages.map(({units}, i) => (
							<MenuItem key={"package-" + i} value={units}>
								<DisplayUnitsCount unit={site.bookingUnit} count={units} />
							</MenuItem>
						))}
					</TextField>
				)}
				{"Unit" === site?.pricingMode && (
					<NumberField
						required
						fullWidth
						label={t("dates.hours")}
						numberFormat={IntegerNumberFormat}
						numberValue={hours ?? null}
						onChangeNumber={handleHoursChange}
						InputLabelProps={{
							shrink: true,
						}}
					/>
				)}
			</Grid>
		</Grid>
	);
}
